/**
 * The formatters this screen's projections take as parameters — `model/` here is pure, so
 * anything that words a date or a decimal takes one in. Two callers: `useTrainerHome` for the
 * queue's rows, and the feedback cards for their own readouts.
 *
 * ── The date rule, and why it is not `new Date(iso)` ─────────────────────────────────────────
 * A civil date the wire sends as `YYYY-MM-DD` is read VERBATIM everywhere in this app: `new Date`
 * parses it as UTC midnight and renders a day early for any reader west of Greenwich. The inputs
 * here are either that shape or a full instant, and both are only ever WORDED — the day the sentence
 * names is the reader's own. `parseIsoDate` answers `null` for anything that is not a civil date,
 * which is what sends an instant down the second path.
 *
 * An unparseable string comes back as itself: a card printing a raw `2026-13-40` is a wire defect
 * the trainer can report, and "Invalid Date" is one they cannot.
 *
 * Both formatters come from `shared/lib/intlCache`, which is what stops a per-row `Intl` constructor.
 */

import { useTranslation } from 'react-i18next';

import { parseIsoDate } from '~/shared/lib/calendarDate';
import { dateTimeFormat, numberFormat } from '~/shared/lib/intlCache';

export interface HomeFormats {
  /** A civil date or an instant, as `12 września` in the reader's own language. */
  readonly formatDate: (iso: string) => string;
  /**
   * The same, WITH the year — `4 czerwca 1990`.
   *
   * A second format rather than a year on the first: everything the queue's own rows date happened
   * inside the last few weeks, and a year on "nagrany 6 lipca" is noise. The one place a year is
   * load-bearing is a survey ANSWER, which can be a date of birth, and a date of birth without one
   * is not a date.
   */
  readonly formatDay: (iso: string) => string;
  /** One decimal place at most, in the reader's own separator — `6,1` in Polish, `6.1` in English. */
  readonly decimal: (value: number) => string;
  /** `22 wrz` — the task cards' short date, in a line where the month's full name is noise. */
  readonly shortDate: (iso: string) => string;
  /** `wt 22` — a civil day as the big figure of a missed session, and a week square's head. */
  readonly weekdayDay: (iso: string) => string;
  /** „wt 22 wrz" — a table's date column. */
  readonly weekdayDate: (iso: string) => string;
  /** `pon 21 wrz, 18:04` — an instant with its hour. */
  readonly dateTime: (iso: string) => string;
}

export function useHomeFormats(): HomeFormats {
  const { i18n } = useTranslation();

  return {
    formatDate: (iso: string) => worded(iso, { day: 'numeric', month: 'long' }),
    formatDay: (iso: string) => worded(iso, { day: 'numeric', month: 'long', year: 'numeric' }),
    decimal: (value: number) =>
      numberFormat(i18n.language, { maximumFractionDigits: 1 }).format(value),
    shortDate: (iso: string) => worded(iso, { day: 'numeric', month: 'short' }),
    weekdayDay: (iso: string) => weekdayAndDay(iso),
    weekdayDate: (iso: string) => `${weekdayAndDay(iso)} ${worded(iso, { month: 'short' })}`,
    dateTime: (iso: string) =>
      worded(iso, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
  };

  /**
   * "wt 22" — the weekday's short name and the day, and nothing between them. `Intl` joins the two
   * with the locale's own punctuation ("wt., 22"), which reads as a list rather than as a day.
   */
  function weekdayAndDay(iso: string): string {
    const at = parseIsoDate(iso) ?? new Date(iso);
    if (Number.isNaN(at.getTime())) return iso;
    const weekday = dateTimeFormat(i18n.language, { weekday: 'short' }).format(at).replace(/\.$/, '');
    return `${weekday} ${dateTimeFormat(i18n.language, { day: 'numeric' }).format(at)}`;
  }

  function worded(iso: string, options: Intl.DateTimeFormatOptions): string {
    const at = parseIsoDate(iso) ?? new Date(iso);
    if (Number.isNaN(at.getTime())) return iso;
    return dateTimeFormat(i18n.language, options).format(at);
  }
}
